import { ChainIdEnum } from "../../enums/chain";
import { CHAIN_CONFIGS } from "./configs";
import { getChainIdFromEnv } from "./getters";
import {
  BridgeAddresses,
  ERC20Addresses,
  MultichainAddresses,
  PerpetualDexContracts,
  SigningDomain,
} from "./types";

/**
 * Get ERC20 addresses for a chain
 */
export const getERC20Addresses = (
  chainId?: ChainIdEnum
): ERC20Addresses | undefined => {
  const id = chainId ?? getChainIdFromEnv();
  const config = CHAIN_CONFIGS.get(id);
  return config?.contracts.erc20;
};

/**
 * Get bridge addresses for a chain
 */
export const getBridgeAddresses = (
  chainId?: ChainIdEnum
): BridgeAddresses | undefined => {
  const id = chainId ?? getChainIdFromEnv();
  const config = CHAIN_CONFIGS.get(id);
  return config?.contracts.bridge;
};

/**
 * Get multichain (Socket) addresses for a chain
 */
export const getMultichainAddresses = (
  chainId?: ChainIdEnum
): MultichainAddresses | undefined => {
  const id = chainId ?? getChainIdFromEnv();
  const config = CHAIN_CONFIGS.get(id);
  return config?.contracts.multichain;
};

/**
 * Get perpetual DEX addresses for a chain
 */
export const getPerpetualDexAddresses = (
  chainId?: ChainIdEnum
): PerpetualDexContracts | undefined => {
  const id = chainId ?? getChainIdFromEnv();
  const config = CHAIN_CONFIGS.get(id);
  return config?.contracts.perpetualDex;
};

/**
 * Get market address for a specific market on the current chain
 */
export const getMarketAddress = (
  marketSymbol: string,
  chainId?: ChainIdEnum
): string | undefined => {
  const dexAddresses = getPerpetualDexAddresses(chainId);
  return dexAddresses?.markets?.[marketSymbol];
};

/**
 * Get all market addresses for the current chain
 */
export const getMarketAddresses = (
  chainId?: ChainIdEnum
): Record<string, string> | undefined => {
  const dexAddresses = getPerpetualDexAddresses(chainId);
  return dexAddresses?.markets;
};

/**
 * Get signing domain for a chain (EIP-712).
 *
 * @throws when the chain has no configured domain. Signing under a fabricated
 * `{ name: "Unknown" }` domain produces a signature no backend can verify, and
 * the failure then surfaces far away as a generic "invalid signature" — so an
 * unconfigured chain is an error here rather than wrong data downstream.
 */
export const getSigningDomain = (chainId?: ChainIdEnum): SigningDomain => {
  const id = chainId ?? getChainIdFromEnv();
  const config = CHAIN_CONFIGS.get(id);
  if (!config?.signingDomain) {
    throw new Error(
      `[ChainConfig] No EIP-712 signing domain configured for chain ${id}. ` +
        "Add one to its entry in src/config/chains/configs."
    );
  }
  return config.signingDomain;
};

/**
 * Get Socket L2 chain ID for bridging
 */
export const getSocketL2ChainId = (
  chainId?: ChainIdEnum
): number | undefined => {
  const id = chainId ?? getChainIdFromEnv();
  const config = CHAIN_CONFIGS.get(id);
  return config?.socketL2ChainId;
};

/**
 * Check if current chain is a testnet
 */
export const isTestnet = (chainId?: ChainIdEnum): boolean => {
  const id = chainId ?? getChainIdFromEnv();
  const config = CHAIN_CONFIGS.get(id);
  return config?.isTestnet ?? true;
};

/**
 * Check if current chain is mainnet
 */
export const isMainnet = (chainId?: ChainIdEnum): boolean => {
  return !isTestnet(chainId);
};

/**
 * Check if native gas token is the quote token (e.g., Arc where native = USDC)
 * On these chains, the USDC ERC20 address points to WNATIVE (wrapped native).
 * Users hold native USDC as gas + may hold wrapped WNATIVE, so balance = native + ERC20.
 */
export const isNativeGasQuoteToken = (chainId?: ChainIdEnum): boolean => {
  const id = chainId ?? getChainIdFromEnv();
  const config = CHAIN_CONFIGS.get(id);
  return config?.nativeIsQuoteToken === true;
};

/**
 * True when WETH is a standalone ERC-20 collateral on this chain (NOT the wrapped
 * native gas token). On such chains (e.g. DogeOS) a WETH deposit must read the
 * ERC-20 balanceOf, not the native balance.
 */
export const isWethErc20Collateral = (chainId?: ChainIdEnum): boolean => {
  const id = chainId ?? getChainIdFromEnv();
  const config = CHAIN_CONFIGS.get(id);
  return config?.wethIsErc20Collateral === true;
};

/**
 * Get chain name
 */
export const getChainName = (chainId?: ChainIdEnum): string => {
  const id = chainId ?? getChainIdFromEnv();
  const config = CHAIN_CONFIGS.get(id);
  return config?.name ?? "Unknown";
};

/**
 * Get chain short name
 */
export const getChainShortName = (chainId?: ChainIdEnum): string => {
  const id = chainId ?? getChainIdFromEnv();
  const config = CHAIN_CONFIGS.get(id);
  return config?.shortName ?? "unknown";
};
